import React, {useState, useRef, useCallback, useEffect} from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {useStores} from '../../../Hooks';
import {MembersRequester} from '../../Domain/Groups/MembersRequester';
import {convertExcelToText} from '../../../Utilities/FileParsingUtility/ExcelParsing';
import EmailInputStep from './EmailInputStep';

const validateEmailsOnServer = async (workspace, emails, markDuplicates) => {
    const addresses = emails.filter((e) => !e.isInvalid).map((e) => e.address);
    if (!addresses.length) return true;
    const duplicates = await MembersRequester.validateEmails(workspace, addresses);
    if (duplicates?.length) {
        markDuplicates(duplicates);
        return false;
    }
    return true;
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['csv', 'xls', 'xlsx'];
const EXCEL_EXTENSIONS = ['xls', 'xlsx'];
const MAX_CELL_LENGTH = 5000;
const BINARY_PATTERN = /^data:|[\x00-\x08\x0E-\x1F]/;

const cleanCsvText = (text) =>
    text.split('\n').map((line) =>
        line.split(',').filter((cell) =>
            cell.length <= MAX_CELL_LENGTH && !BINARY_PATTERN.test(cell),
        ).join(','),
    ).filter((line) => line.trim()).join('\n');

const Step1Emails = ({emailHook, available, total, showCsvUpload, onClose, onContinue}) => {
    const {modalStore, generalStore} = useStores();
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleUploadCsv = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        const ext = file.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            modalStore.showErrorMessage('Only CSV and Excel files are allowed');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            modalStore.showErrorMessage('File size must be under 5MB');
            return;
        }

        setIsUploading(true);
        try {
            const isExcel = EXCEL_EXTENSIONS.includes(ext);
            const rawText = isExcel
                ? await convertExcelToText(file)
                : await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (event) => resolve(event.target.result);
                    reader.onerror = () => reject(new Error('Failed to read file'));
                    reader.readAsText(file);
                });
            const text = cleanCsvText(rawText);

            const emails = await MembersRequester.extractEmailsFromFile(generalStore.workspace, text);
            if (emails?.length) {
                emailHook.addEmails(emails.join(','));
            } else {
                modalStore.showInfoMessage('No emails found in file');
            }
        } catch (error) {
            console.error('File upload failed:', error);
            const serverError = error?.response?.data?.error;
            if (serverError === 'AI_CREDITS_EXCEEDED') {
                modalStore.showErrorMessage('Can\'t extract email addresses, AI credits limit exceeded');
            } else {
                modalStore.showErrorMessage('Failed to extract emails from file');
            }
        } finally {
            setIsUploading(false);
        }
    }, [emailHook, generalStore.workspace, modalStore]);

    const [isValidating, setIsValidating] = useState(false);
    const exceedsSeats = emailHook.validEmails.length > available;
    const canContinue = emailHook.emails.length > 0 && emailHook.invalidCount === 0 && emailHook.duplicateCount === 0 && !exceedsSeats && !isValidating;

    const handleContinue = useCallback(async () => {
        if (!canContinue) return;
        setIsValidating(true);
        try {
            const isValid = await validateEmailsOnServer(generalStore.workspace, emailHook.emails, emailHook.markDuplicates);
            if (isValid) onContinue();
        } catch (error) {
            console.error('Email validation failed:', error);
        } finally {
            setIsValidating(false);
        }
    }, [canContinue, generalStore.workspace, emailHook.emails, emailHook.markDuplicates, onContinue]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter' && !emailHook.inputValue.trim() && canContinue && !isValidating) {
                e.preventDefault();
                handleContinue();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [emailHook.inputValue, canContinue, isValidating, handleContinue]);

    return (
        <>
            <EmailInputStep
                emailHook={emailHook}
                available={available}
                total={total}
                onUploadCsv={showCsvUpload ? handleUploadCsv : null}
                isUploading={isUploading}
                exceedsSeats={exceedsSeats}
            />
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{display: 'none'}}
                onChange={handleFileChange}
            />
            <div className="flex justify-end gap-3 pt-4">
                <div
                    className="rounded-button px-4 py-2 text-sm font-bold cursor-pointer text-main-normal hover:bg-panels-normal"
                    onClick={onClose}
                >
                    Cancel
                </div>
                <div
                    data-testid="invite-step1-continue"
                    className={`rounded-button px-4 py-2 text-sm font-bold flex items-center gap-1 text-classic-button-normal-text ${canContinue ? 'bg-classic-button-normal hover:bg-classic-button-hover cursor-pointer' : 'bg-classic-button-disabled cursor-default'}`}
                    onClick={handleContinue}
                >
                    {isValidating ? 'Validating...' : 'Continue'}
                    {!isValidating && <ChevronRightIcon style={{fontSize: 18}} />}
                </div>
            </div>
        </>
    );
};

export default Step1Emails;
